import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UserRuleMastery } from '@proref/types';
import { useMasteryQuery } from '../../hooks/use-mastery';
import { Skeleton } from '../shared/Skeleton';
import { MasteryRadar } from '../shared/MasteryRadar';
import { AnimatedNumber } from '../shared/AnimatedNumber';

// Per-rule accuracy across all of the referee's answers — live from GET /v1/me/mastery. Left: the
// radar; right: the lowest-accuracy rules to drill next. Both are real (read from user_mastery),
// unlike the streak/daily-loop placeholders elsewhere on Home.

const RADAR_MAX_AXES = 8; // more than this clutters the chart; keep the best-sampled rules
const DRILL_COUNT = 3;

// Accuracy → tone. Red below 60%, amber below 75%, muted otherwise — the same thresholds the
// design uses to flag weak rules.
function accuracyTone(score: number): string {
  if (score < 0.6) return 'text-danger';
  if (score < 0.75) return 'text-warn';
  return 'text-muted';
}

export function RuleMasteryCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useMasteryQuery(true);
  const rules = data ?? [];

  // Radar axes: cap to the best-sampled rules (most attempts), then restore article-number order so
  // the axes stay stable. The service already sorts by article_number, so slicing after re-sort keeps
  // that guarantee.
  const radarRules =
    rules.length <= RADAR_MAX_AXES
      ? rules
      : [...rules]
          .sort((a, b) => b.attempts_count - a.attempts_count)
          .slice(0, RADAR_MAX_AXES)
          .sort((a, b) => rules.indexOf(a) - rules.indexOf(b));

  // Drill list: lowest accuracy first, tie-broken by more attempts (a well-sampled weak rule beats a
  // one-off miss) — mirrors the weakest-rule tie-break in getMeStats.
  const drill = [...rules]
    .sort((a, b) => a.accuracy_score - b.accuracy_score || b.attempts_count - a.attempts_count)
    .slice(0, DRILL_COUNT);

  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-ink">{t('home.mastery.title')}</h2>
          <p className="mt-0.5 text-xs text-muted">{t('home.mastery.subtitle')}</p>
        </div>
        <Link
          to="/rule-tests"
          className="flex-none text-xs font-medium text-accent hover:text-accent-2"
        >
          {t('home.mastery.takeTest')}
        </Link>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <Skeleton className="h-40 w-40 flex-none rounded-full" />
            <div className="w-full space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          </div>
        ) : isError && !data ? (
          <div className="rounded border border-dashed border-danger bg-danger-tint px-4 py-6 text-center">
            <p className="text-xs text-danger">{t('home.mastery.couldNotLoad')}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-1.5 text-[11px] font-medium text-ink underline underline-offset-2 hover:text-ink-2"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        ) : rules.length === 0 ? (
          <div className="rounded border border-dashed border-line-2 bg-bg-2 px-4 py-10 text-center">
            <p className="text-xs font-medium text-ink">{t('home.mastery.none')}</p>
            <p className="mt-1 text-[11px] text-muted">{t('home.mastery.answerToBuild')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
            {/* The radar needs ≥3 axes to form a polygon; with 1–2 rules answered, lead with the
                drill list and skip the chart rather than draw a degenerate shape. */}
            {radarRules.length >= 3 ? (
              <div className="h-44 w-44 flex-none">
                <MasteryRadar rules={radarRules} />
              </div>
            ) : (
              <div className="grid h-44 w-44 flex-none place-items-center rounded-sm bg-bg-2 px-4 text-center text-[11px] text-muted">
                {t('home.mastery.needMoreRules')}
              </div>
            )}

            <div className="w-full min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">
                {t('home.mastery.drillNext')}
              </div>
              <ul className="mt-2.5 space-y-2">
                {drill.map((rule) => (
                  <li key={rule.rule_article_id} className="flex items-center gap-2.5">
                    <span className="grid h-6 w-6 flex-none place-items-center rounded-sm bg-bg-2 text-[11px] font-semibold text-ink-2">
                      {rule.article_number.split(':')[0]}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{rule.title}</span>
                    <AnimatedNumber
                      value={rule.accuracy_score * 100}
                      format={(n) => `${Math.round(n)}%`}
                      className={`flex-none text-xs font-semibold ${accuracyTone(rule.accuracy_score)}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
